/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { AdnamiSkinAdapter } from './vendor/adnami/skins';
import { CeltraBannerAdapter } from './vendor/celtra/banner';

import type { FormatDetection, VendorFormat } from '../../types/formats';

type FormatInteractionCallback = (trigger: string) => void;

/**
 * Vendor-specific detection and interaction capture for takeover ad formats
 * (skins, wallpapers, seamless backgrounds). One adapter instance is owned by
 * one Placement and scoped to a single render; the placement disposes it on
 * re-render.
 *
 * The seam is deliberately narrow. Vendor formats live under
 * `formats/vendor/<vendor>/<format>.ts` (one file per format, so a vendor
 * with several formats stays one folder); site-managed formats with no
 * third-party vendor get their own package alongside `vendor/`. Adding
 * support = a new format file plus an entry in VENDOR_FORMAT_REGISTRY.
 */
interface VendorFormatAdapter {
  readonly vendor: string;
  // Synchronous detection against the rendered slot container (and optional
  // Prebid bid markup). Returns null when the render is not this format.
  inspectRender(container: HTMLElement | null, bidAdMarkup?: string): FormatDetection | null;
  // Bounded async re-check for markers injected after render; fires the
  // callback at most once.
  watchForLateDetection(
    container: HTMLElement | null,
    callback: (detection: FormatDetection) => void,
  ): void;
  // Arm click capture on the format's surfaces outside the host container.
  armInteractionCapture(onInteraction: FormatInteractionCallback): void;
  dispose(): void;
}

type VendorFormatEntry = {
  vendor: VendorFormat;
  // Cheap page-environment probe (typeof checks only) deciding whether this
  // vendor is relevant on the current page. Re-evaluated per render so
  // late-loading vendor engines are picked up on subsequent renders.
  isPresent: () => boolean;
  create: () => VendorFormatAdapter;
};

const VENDOR_FORMAT_REGISTRY: VendorFormatEntry[] = [
  {
    vendor: 'adnami',
    isPresent: () => typeof window.adsm !== 'undefined' || typeof window.adsmtag !== 'undefined',
    create: () => new AdnamiSkinAdapter(),
  },
  {
    vendor: 'celtra',
    // Celtra exposes no top-window global — its runtime lives entirely inside
    // the rendered ad iframe — so page-level presence cannot be probed. The
    // adapter is always created; relevance is decided per render by
    // inspectRender's marker scan (a few scoped querySelectors).
    isPresent: () => true,
    create: () => new CeltraBannerAdapter(),
  },
];

/**
 * Returns an adapter for every vendor the caller can host whose engine is
 * present on the page right now: `supported` is the caller's declared vendor
 * list (a placement only hosts the formats trafficked into its slot kind), and
 * an empty list yields no adapters and runs no probes. The caller-owned cache
 * preserves adapter instances across calls (per-render state and teardown stay
 * placement-scoped) while probes are re-evaluated every call.
 */
function createVendorFormatAdapters(
  cache: Map<string, VendorFormatAdapter>,
  supported: readonly VendorFormat[],
): VendorFormatAdapter[] {
  const adapters: VendorFormatAdapter[] = [];
  for (const entry of VENDOR_FORMAT_REGISTRY) {
    // Declared-support check first: it is cheaper than the probe, and a vendor
    // the caller cannot host should not be probed for at all.
    if (!supported.includes(entry.vendor) || !entry.isPresent()) {
      continue;
    }
    let adapter = cache.get(entry.vendor);
    if (!adapter) {
      adapter = entry.create();
      cache.set(entry.vendor, adapter);
    }
    adapters.push(adapter);
  }
  return adapters;
}

export { createVendorFormatAdapters };
export type { FormatInteractionCallback, VendorFormatAdapter, VendorFormatEntry };
